import Image from "next/image";
import Link from "next/link";
import type { GetStaticProps } from "next";
import { allWordpressPosts } from "contentlayer/generated";
import BlogCard, { type BlogCardProps } from "../components/BlogCard";
import BlogServiceCta from "../components/BlogServiceCta";
import Seo from "../components/Seo";
import { blogCategories, buildExcerpt, formatCategoryLabel, formatThaiDate, pickFallbackImage, parseGmtDate } from "../lib/posts";

type BlogPreview = BlogCardProps & { publishedAt: string; publishedLabel: string };
type CategorySection = {
  slug: string;
  label: string;
  description: string;
  count: number;
  posts: BlogPreview[];
};
type BlogPageProps = {
  featuredPost: BlogPreview | null;
  recentPosts: BlogPreview[];
  categorySections: CategorySection[];
  totalPosts: number;
};

export default function BlogPage({ featuredPost, recentPosts, categorySections, totalPosts }: BlogPageProps) {
  const title = "บทความบัญชี ภาษี และธุรกิจสำหรับผู้ประกอบการ";
  const siteUrl = (process.env.NEXT_PUBLIC_SITE_URL ?? "https://www.kmcpaccounting.com").replace(/\/$/, "");
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${siteUrl}/blog#webpage`,
        url: `${siteUrl}/blog`,
        name: title,
        inLanguage: "th-TH",
        isPartOf: { "@id": `${siteUrl}/#website` }
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "หน้าแรก", item: `${siteUrl}/` },
          { "@type": "ListItem", position: 2, name: "บทความ", item: `${siteUrl}/blog` }
        ]
      }
    ]
  };

  return (
    <>
      <Seo
        title={title}
        description="รวมบทความบัญชี ภาษี จดทะเบียนธุรกิจ เอกสาร และการบริหารพนักงานสำหรับผู้ประกอบการไทย จัดหมวดหมู่เพื่อค้นหาเนื้อหาได้ง่าย"
        path="/blog"
        jsonLd={structuredData}
      />

      <header className="border-b border-[#e8d875] bg-[#fff9d9]">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 sm:px-8 lg:grid-cols-[1fr_18rem] lg:items-end lg:py-20">
          <div>
            <nav aria-label="เส้นทางนำทาง" className="mb-8">
              <ol className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <li><Link href="/" className="rounded-sm hover:text-gray-950 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950">หน้าแรก</Link></li>
                <li className="text-slate-400" aria-hidden>/</li>
                <li aria-current="page" className="font-medium text-gray-950">บทความ</li>
              </ol>
            </nav>
            <p className="text-sm font-semibold tracking-[0.16em] text-[#806500]">KMCP KNOWLEDGE</p>
            <h1 className="mt-5 max-w-4xl text-4xl font-semibold leading-tight text-gray-950 sm:text-5xl lg:text-6xl">
              เรื่องบัญชีและภาษี<br className="hidden sm:block" /> ที่เจ้าของธุรกิจอ่านรู้เรื่อง
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-slate-700 sm:text-lg">
              ค้นหาคำตอบตามเรื่องที่กำลังจัดการ ตั้งแต่เริ่มธุรกิจ งานบัญชีรายเดือน ภาษี เอกสาร ไปจนถึงพนักงานและเงินเดือน
            </p>
          </div>
          <div className="border-l-2 border-[#d2b91f] pl-6">
            <p className="text-4xl font-semibold text-gray-950">{totalPosts}</p>
            <p className="mt-1 text-sm leading-6 text-slate-600">บทความใน 6 หมวดความรู้</p>
            <Link href="/blog/archive" className="mt-5 inline-flex min-h-11 items-center text-sm font-semibold text-gray-950 hover:underline">
              ดูคลังบทความทั้งหมด →
            </Link>
          </div>
        </div>
      </header>

      <nav className="sticky top-16 z-20 border-b border-slate-200 bg-white/95 backdrop-blur" aria-label="หมวดหมู่บทความ">
        <div className="mx-auto max-w-7xl overflow-x-auto px-6 sm:px-8">
          <ul className="flex min-w-max items-center gap-1 py-3">
            {categorySections.map((category) => (
              <li key={category.slug}>
                <a href={`#${category.slug}`} className="inline-flex min-h-11 items-center rounded-full px-4 text-sm font-semibold text-slate-600 transition hover:bg-[#fff9d9] hover:text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950">
                  {category.label} <span className="ml-2 text-xs text-slate-400">{category.count}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <main className="bg-white">
        {featuredPost ? (
          <section className="mx-auto max-w-7xl px-6 py-16 sm:px-8 lg:py-20" aria-labelledby="featured-heading">
            <div className="mb-8 flex items-end justify-between gap-6">
              <div>
                <p className="text-sm font-semibold text-[#806500]">เริ่มอ่านจากตรงนี้</p>
                <h2 id="featured-heading" className="mt-2 text-3xl font-semibold text-gray-950 sm:text-4xl">บทความแนะนำ</h2>
              </div>
              <Link href="/blog/archive" className="hidden min-h-11 items-center text-sm font-semibold text-gray-950 hover:underline sm:inline-flex">
                ดูทั้งหมด →
              </Link>
            </div>

            <div className="grid gap-6 lg:grid-cols-[1.45fr_0.75fr]">
              <article className="group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_60px_-42px_rgba(15,23,42,0.45)]">
                <div className="grid h-full md:grid-cols-[1.08fr_0.92fr]">
                  <div className="relative min-h-72 overflow-hidden bg-[#f4cf21] md:min-h-[28rem]">
                    <Image src={featuredPost.image ?? "/images/blog-boi.svg"} alt={featuredPost.imageAlt ?? featuredPost.title} fill priority className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.025]" sizes="(min-width: 1024px) 42vw, 100vw" />
                  </div>
                  <div className="flex flex-col p-7 sm:p-9">
                    <div className="flex flex-wrap items-center gap-3 text-xs font-semibold">
                      <span className="text-[#806500]">{featuredPost.category}</span>
                      <span className="text-slate-300" aria-hidden>•</span>
                      <time className="text-slate-500" dateTime={featuredPost.publishedAt}>{featuredPost.publishedLabel}</time>
                    </div>
                    <h3 className="mt-5 text-2xl font-semibold leading-snug text-gray-950 sm:text-3xl">
                      <Link href={featuredPost.href} className="after:absolute after:inset-0 focus-visible:outline-none">{featuredPost.title}</Link>
                    </h3>
                    <p className="mt-4 line-clamp-4 text-sm leading-7 text-slate-600">{featuredPost.description}</p>
                    <span className="mt-auto pt-8 text-sm font-semibold text-gray-950" aria-hidden>อ่านบทความฉบับเต็ม →</span>
                  </div>
                </div>
              </article>

              <div className="grid gap-4">
                {recentPosts.map((post, index) => (
                  <article key={post.href} className="group relative flex min-h-52 flex-col justify-between rounded-3xl border border-slate-200 bg-[#f8f8f6] p-6 transition hover:border-[#d4bc3e] hover:bg-[#fffdf3] sm:p-7">
                    <div>
                      <div className="flex items-center justify-between gap-4 text-xs font-semibold">
                        <span className="text-[#806500]">{post.category}</span>
                        <span className="text-slate-400">0{index + 2}</span>
                      </div>
                      <h3 className="mt-5 line-clamp-3 text-xl font-semibold leading-snug text-gray-950">
                        <Link href={post.href} className="after:absolute after:inset-0 focus-visible:outline-none">{post.title}</Link>
                      </h3>
                    </div>
                    <span className="mt-6 text-sm font-semibold text-gray-950" aria-hidden>อ่านต่อ →</span>
                  </article>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        <section className="border-y border-slate-200 bg-[#f7f8fa]">
          <div className="mx-auto max-w-7xl px-6 py-14 sm:px-8 lg:py-16">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold text-[#806500]">เลือกตามเรื่องที่ต้องการรู้</p>
              <h2 className="mt-2 text-3xl font-semibold text-gray-950 sm:text-4xl">หมวดหมู่บทความ</h2>
              <p className="mt-4 text-base leading-8 text-slate-600">แต่ละหมวดรวบรวมเนื้อหาที่เกี่ยวข้องไว้ด้วยกัน เพื่อให้ค้นหาคำตอบต่อเนื่องได้ง่ายขึ้น</p>
            </div>
          </div>
        </section>

        {categorySections.map((category, categoryIndex) => (
          <section key={category.slug} id={category.slug} className={`scroll-mt-36 ${categoryIndex % 2 === 1 ? "bg-[#fafaf8]" : "bg-white"}`} aria-labelledby={`${category.slug}-heading`}>
            <div className="mx-auto max-w-7xl px-6 py-16 sm:px-8 lg:py-20">
              <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-[#806500]">0{categoryIndex + 1}</span>
                    <span className="h-px w-8 bg-[#d2b91f]" aria-hidden />
                    <span className="text-sm text-slate-500">{category.count} บทความ</span>
                  </div>
                  <h2 id={`${category.slug}-heading`} className="mt-4 text-3xl font-semibold text-gray-950 sm:text-4xl">{category.label}</h2>
                  <p className="mt-3 text-base leading-7 text-slate-600">{category.description}</p>
                </div>
                <Link href="/blog/archive" className="inline-flex min-h-11 items-center text-sm font-semibold text-gray-950 hover:underline">
                  ดูบทความทั้งหมดในคลัง →
                </Link>
              </div>
              <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {category.posts.map((post) => <BlogCard key={post.href} {...post} />)}
              </div>
            </div>
          </section>
        ))}

        <div className="mx-auto max-w-7xl px-6 pb-20 sm:px-8 lg:pb-24">
          <BlogServiceCta placement="footer" />
        </div>
      </main>
    </>
  );
}

export const getStaticProps: GetStaticProps<BlogPageProps> = async () => {
  const posts = allWordpressPosts
    .filter((post) => post.status === "publish")
    .sort((a, b) => parseGmtDate(b.dateGmt || b.date).getTime() - parseGmtDate(a.dateGmt || a.date).getTime());

  const toPreview = (post: (typeof posts)[number], index: number): BlogPreview => ({
    category: formatCategoryLabel(post.primaryCategory),
    title: post.title,
    description: buildExcerpt(post),
    href: `/blog/${post.seoSlug ?? post.slug}`,
    image: post.heroImage || pickFallbackImage(index),
    imageAlt: post.heroImageAlt || post.title,
    publishedAt: parseGmtDate(post.dateGmt || post.date).toISOString(),
    publishedLabel: formatThaiDate(post)
  });

  const categorySections = blogCategories.map((category) => {
    const categoryPosts = posts.filter((post) => post.primaryCategory === category.slug);
    return {
      ...category,
      count: categoryPosts.length,
      posts: categoryPosts.slice(0, 3).map(toPreview)
    };
  });

  return {
    props: {
      featuredPost: posts[0] ? toPreview(posts[0], 0) : null,
      recentPosts: posts.slice(1, 3).map(toPreview),
      categorySections,
      totalPosts: posts.length
    }
  };
};
